import type { ReactNode } from "react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useGlobal } from "@/app/store/global"
import { Button } from "@/shared/components"
import { Logout, User } from "@/shared/components/ui/icons"
import { useTranslatedRole } from "@/shared/hooks/use-translated-role.ts"
import { withTransition } from "@/shared/lib/helper-functions"

type IDropdownProps = {
  label: ReactNode
  children: ReactNode
}

export const Dropdown = ({ label, children }: IDropdownProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open])

  return (
    <div className="relative z-[3000] inline-block" ref={ref}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") setOpen(o => !o)
        }}
        className="cursor-pointer"
      >
        {label}
      </div>
      {open && <div className="absolute right-0 z-10 mt-2 min-w-48 rounded-xl border bg-ui-background-base shadow-lg">{children}</div>}
    </div>
  )
}

export const UserProfile = () => {
  // Helpers
  const translatedRole = useTranslatedRole()
  const navigate = useNavigate()
  const { t } = useTranslation("common")

  // Store
  const me = useGlobal(state => state.me)

  return (
    <Dropdown
      label={
        <div className="flex gap-2">
          <div className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-background">
            <User size={20} className="text-icon-color" />
          </div>
          <div className="flex flex-col items-start justify-center">
            <div className="text-base text-text-color" data-testid="profile-user-name">
              {me?.name}
            </div>
            <div className="text-DarkGray text-sm" data-testid="profile-user-role">
              {/* {translatedRole(me?.base_role)} */}
              {me?.roles[0]?.name || translatedRole(me?.base_role)}
            </div>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-1">
          {me?.company_name && (
            <>
              <span className="text-DarkGray text-xs">{t("company")}</span>
              <span className="text-sm text-text-color">{me.company_name}</span>
            </>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-DarkGray text-xs">{t("email")}</span>
          <span className="text-sm text-text-color">{me?.email}</span>
        </div>
        <hr />
        <Button
          startIcon={<Logout size={20} className="" />}
          className="w-full justify-start"
          variant="text"
          color="error"
          onClick={() => {
            withTransition(() => {
              useGlobal.logout()
              navigate("/auth/login")
            })
          }}
        >
          {t("logout")}
        </Button>
      </div>
    </Dropdown>
  )
}
